import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { connect } from 'react-redux';
import { AlertCircle, ArrowRight, CheckCircle2, Loader2, LocateFixed, X } from 'lucide-react';
import { checkAuthenticated, load_user } from '../actions/auth';
import { getAccessToken } from '../utils/authToken';
import { startAuthKeepAlive } from '../utils/authSession';
import { routeToNextStep } from '../utils/flowGuards';
import { withIncomingSearch } from '../utils/landingQuery';
import apiClient from '../utils/axiosConfig';
import {
    readActiveTripGeneration,
    TRIP_GENERATION_EVENT,
    tripGenerationEstimate,
    tripGenerationRecordIsGone,
    tripGenerationStageCopy,
    writeActiveTripGeneration,
} from '../utils/tripGenerationStatus';
import {
    announceOptionsReady,
    OPTIONS_READY_EVENT,
    readOptionsReady,
    releaseOptionsReadyTitle,
    writeOptionsReady,
} from '../utils/optionsReadyNotice';
import { affiliateCodeOnLocation, captureAffiliateRefFromLocation, reportAffiliateClick } from '../utils/affiliateRef';
import { persistPaystepSearch } from '../utils/paystepParams';
import {
    isSurveyPath,
    shouldDeferNonMicPermissions,
    surveyNonMicPermissionsReleased,
    SURVEY_NON_MIC_PERMISSIONS_EVENT,
} from '../utils/surveyPermissionGate';
import {
    RUNTIME_LOCATION_REFRESH_MS,
    readRuntimeLocationCurrent,
    readRuntimeLocationSync,
    syncBrowserRuntimeLocation,
} from '../utils/runtimeLocation';
import { locationPromptAllowedOnPath } from '../utils/locationPromptPath';
import './Layout.css';

const browserGeolocationAvailable = () => (
    typeof window !== 'undefined' &&
    window.isSecureContext &&
    typeof navigator !== 'undefined' &&
    Boolean(navigator.geolocation)
);

const runtimeLocationSyncRecentlyAttempted = () => {
    const prior = readRuntimeLocationSync();
    return Boolean(
        (readRuntimeLocationCurrent() || ['denied', 'unavailable'].includes(prior.state))
        && Date.now() - Number(prior.synced_at || 0) < RUNTIME_LOCATION_REFRESH_MS
    );
};

let runtimeLocationSyncInFlight = false;

const syncRuntimeLocationInBackground = ({ pathname = '', captureReason = '' } = {}) => {
    if (!browserGeolocationAvailable() || runtimeLocationSyncInFlight || runtimeLocationSyncRecentlyAttempted()) {
        return;
    }
    runtimeLocationSyncInFlight = true;
    syncBrowserRuntimeLocation({ pathname, captureReason })
        .catch(() => {
            // Runtime location is an enhancement; never interrupt the app shell for it.
        })
        .finally(() => {
            runtimeLocationSyncInFlight = false;
        });
};

const LOCATION_GATE_DISMISS_KEY = 'dople_location_gate_dismissed_v1';

const readLocationGateDismissed = () => {
    try {
        return window.sessionStorage.getItem(LOCATION_GATE_DISMISS_KEY) === '1';
    } catch {
        return false;
    }
};

const AUTH_RESUME_PATHS = new Set([
    '/',
    '/about',
    '/contact',
    '/guides',
    '/privacy',
    '/terms',
    '/login',
    '/signup',
    '/reset-password',
    '/oauth/callback',
    '/stripe/success',
]);

const shouldResumeAuthenticatedRoute = (pathname = '') => (
    AUTH_RESUME_PATHS.has(pathname)
    || pathname.startsWith('/guides/')
    || pathname.startsWith('/r/')
    || pathname.startsWith('/c/')
    || pathname.startsWith('/password/reset/confirm/')
    || pathname.startsWith('/activate/')
);

const Layout = (props) => {
    const location = useLocation();
    const navigate = useNavigate();
    const {
        checkAuthenticated: checkSessionAuthenticated,
        load_user: loadUser,
        isAuthenticated,
    } = props;
    const [globalError, setGlobalError] = useState('');
    const [runtimeLocationReady, setRuntimeLocationReady] = useState(() => Boolean(readRuntimeLocationCurrent()));
    const [locationPermissionState, setLocationPermissionState] = useState('checking');
    const [surveyNonMicReleased, setSurveyNonMicReleased] = useState(() => surveyNonMicPermissionsReleased());
    const [locationRequestBusy, setLocationRequestBusy] = useState(false);
    const [locationRequestNote, setLocationRequestNote] = useState('');
    const [locationGateDismissed, setLocationGateDismissed] = useState(() => readLocationGateDismissed());
    const [activeTripGeneration, setActiveTripGeneration] = useState(() => readActiveTripGeneration());
    const [optionsReady, setOptionsReady] = useState(() => readOptionsReady());

    const clearActiveTripGeneration = useCallback(() => {
        setActiveTripGeneration(null);
        writeActiveTripGeneration(null);
    }, []);

    useEffect(() => {
        startAuthKeepAlive();
        checkSessionAuthenticated();
        loadUser();
    }, [checkSessionAuthenticated, loadUser]);

    useEffect(() => {
        captureAffiliateRefFromLocation(location);
        persistPaystepSearch(location);
        const landedCode = affiliateCodeOnLocation(location);
        if (landedCode) reportAffiliateClick(landedCode, location);
    }, [location]);

    useEffect(() => {
        const onRuntimeLocation = () => {
            setRuntimeLocationReady(Boolean(readRuntimeLocationCurrent()));
        };
        window.addEventListener('dople:runtime-location', onRuntimeLocation);
        return () => window.removeEventListener('dople:runtime-location', onRuntimeLocation);
    }, []);

    useEffect(() => {
        const syncSurveyPermissions = () => {
            setSurveyNonMicReleased(surveyNonMicPermissionsReleased());
        };
        window.addEventListener(SURVEY_NON_MIC_PERMISSIONS_EVENT, syncSurveyPermissions);
        return () => window.removeEventListener(SURVEY_NON_MIC_PERMISSIONS_EVENT, syncSurveyPermissions);
    }, []);

    useEffect(() => {
        const onOptionsReady = (event) => {
            setOptionsReady(event?.detail || readOptionsReady());
        };
        window.addEventListener(OPTIONS_READY_EVENT, onOptionsReady);
        window.addEventListener('storage', onOptionsReady);
        return () => {
            window.removeEventListener(OPTIONS_READY_EVENT, onOptionsReady);
            window.removeEventListener('storage', onOptionsReady);
        };
    }, []);

    useEffect(() => {
        const tripId = optionsReady?.tripId;
        if (!tripId || optionsReady?.stage !== 'building') return undefined;
        let cancelled = false;
        let timer = null;
        const poll = async () => {
            try {
                const response = await apiClient.get(`/api/trips/${tripId}`, {
                    params: { lightweight: '1' },
                    skipGlobalErrorToast: true,
                });
                if (cancelled) return;
                const trip = response?.data || {};
                const constraints = trip.structured_constraints || {};
                const state = String(constraints.options_generation_state || '').toLowerCase();
                const hasOptions = Array.isArray(trip?.destination_options?.options) && trip.destination_options.options.length > 0;
                if (state === 'complete' || (!['running', 'queued', 'accepted', 'agent_pending'].includes(state) && hasOptions)) {
                    const ready = {
                        ...optionsReady,
                        destination: trip.selected_destination || optionsReady.destination,
                        stage: 'ready',
                    };
                    setOptionsReady(ready);
                    writeOptionsReady(ready);
                    announceOptionsReady();
                    return;
                }
                if (['error', 'failed'].includes(state)) {
                    setOptionsReady(null);
                    writeOptionsReady(null);
                    return;
                }
            } catch (error) {
                if (tripGenerationRecordIsGone(error)) {
                    if (!cancelled) {
                        setOptionsReady(null);
                        writeOptionsReady(null);
                    }
                    return;
                }
            }
            if (!cancelled) timer = window.setTimeout(poll, 5000);
        };
        void poll();
        return () => {
            cancelled = true;
            if (timer) window.clearTimeout(timer);
        };
    }, [optionsReady?.tripId, optionsReady?.stage]);

    const dismissOptionsReady = useCallback(() => {
        setOptionsReady(null);
        writeOptionsReady(null);
        releaseOptionsReadyTitle();
    }, []);

    useEffect(() => {
        const onTripGeneration = (event) => {
            setActiveTripGeneration(event?.detail || readActiveTripGeneration());
        };
        window.addEventListener(TRIP_GENERATION_EVENT, onTripGeneration);
        window.addEventListener('storage', onTripGeneration);
        return () => {
            window.removeEventListener(TRIP_GENERATION_EVENT, onTripGeneration);
            window.removeEventListener('storage', onTripGeneration);
        };
    }, []);

    useEffect(() => {
        const tripId = activeTripGeneration?.tripId;
        if (!tripId || activeTripGeneration?.stage === 'complete') return undefined;
        let cancelled = false;
        let timer = null;
        const poll = async () => {
            try {
                const response = await apiClient.get(`/api/trips/${tripId}`, {
                    params: { lightweight: '1' },
                    skipGlobalErrorToast: true,
                });
                if (cancelled) return;
                const trip = response?.data || {};
                const constraints = trip.structured_constraints || {};
                const state = String(constraints.itinerary_generation_state || '').toLowerCase();
                const stage = constraints.itinerary_generation_stage || activeTripGeneration.stage || 'starting';
                if (Array.isArray(trip?.itinerary?.days) && trip.itinerary.days.length > 0) {
                    const complete = {
                        ...activeTripGeneration,
                        destination: trip.selected_destination || activeTripGeneration.destination,
                        stage: 'complete',
                    };
                    setActiveTripGeneration(complete);
                    writeActiveTripGeneration(complete);
                    return;
                }
                if (['error', 'failed'].includes(state)) {
                    const failed = { ...activeTripGeneration, stage: 'failed' };
                    setActiveTripGeneration(failed);
                    writeActiveTripGeneration(failed);
                    return;
                }
                const next = {
                    ...activeTripGeneration,
                    destination: trip.selected_destination || activeTripGeneration.destination,
                    stage,
                    estimate: activeTripGeneration.estimate || tripGenerationEstimate({
                        startDate: trip.start_date,
                        endDate: trip.end_date,
                        tripType: trip.trip_type,
                        destination: trip.selected_destination || constraints.destination,
                        structuredConstraints: constraints,
                    }),
                };
                setActiveTripGeneration(next);
                writeActiveTripGeneration(next);
            } catch (error) {
                if (tripGenerationRecordIsGone(error)) {
                    // A locally cached job must not outlive the trip it points to.
                    if (!cancelled) clearActiveTripGeneration();
                    return;
                }
                // A transient navigation/network failure should not erase a running job.
            }
            if (!cancelled) timer = window.setTimeout(poll, 7000);
        };
        void poll();
        return () => {
            cancelled = true;
            if (timer) window.clearTimeout(timer);
        };
    // Poll identity is intentionally keyed to the durable trip and lifecycle stage;
    // including the rewritten status object would immediately restart every poll.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTripGeneration?.tripId, activeTripGeneration?.stage, clearActiveTripGeneration]);

    useEffect(() => {
        if (activeTripGeneration?.stage !== 'complete') return undefined;
        const timer = window.setTimeout(() => {
            setActiveTripGeneration(null);
            writeActiveTripGeneration(null);
        }, 12000);
        return () => window.clearTimeout(timer);
    }, [activeTripGeneration?.stage]);

    useEffect(() => {
        const hasStoredSession = isAuthenticated === true || Boolean(getAccessToken());
        const shouldResolveResume = shouldResumeAuthenticatedRoute(location.pathname) && hasStoredSession;

        if (!shouldResolveResume) {
            return undefined;
        }

        let cancelled = false;
        const resumeOnboardingOrApp = async () => {
            try {
                const result = await routeToNextStep(navigate, {
                    replace: true,
                    currentPath: location.pathname,
                    forceResolvedPath: true,
                    silent: true,
                });
                const target = result?.next;
                if (cancelled) return;
                if (target && target !== location.pathname) {
                    navigate(withIncomingSearch(target, location.search, location.hash), { replace: true });
                }
            } catch (err) {
                console.warn('[Layout] Authenticated resume routing failed:', err);
            }
        };

        void resumeOnboardingOrApp();
        return () => {
            cancelled = true;
        };
    }, [isAuthenticated, location.hash, location.pathname, location.search, navigate]);

    useEffect(() => {
        const hasStoredSession = isAuthenticated === true || Boolean(getAccessToken());
        const resumeRedirectPending = hasStoredSession && shouldResumeAuthenticatedRoute(location.pathname);
        if (resumeRedirectPending || shouldDeferNonMicPermissions(location.pathname)) {
            return undefined;
        }
        if (!hasStoredSession) {
            setLocationPermissionState('idle');
            return undefined;
        }
        if (readRuntimeLocationCurrent()) {
            setLocationPermissionState('ready');
            return undefined;
        }
        setLocationPermissionState('checking');
        let cancelled = false;
        const prepareLocation = async () => {
            let permissionState = 'prompt';
            try {
                if (navigator.permissions?.query) {
                    const status = await navigator.permissions.query({ name: 'geolocation' });
                    permissionState = status?.state || permissionState;
                }
            } catch {
                // Browsers without Permissions API still get the visible consent step.
            }
            if (cancelled) return;
            setLocationPermissionState(permissionState);
            if (permissionState === 'granted') {
                syncRuntimeLocationInBackground({
                    pathname: location.pathname,
                    captureReason: 'authenticated_session_granted',
                });
                return;
            }
            setLocationRequestNote(
                permissionState === 'denied'
                    ? 'Location is blocked in Chrome. You can enable it from the site controls beside the address bar.'
                    : ''
            );
        };
        void prepareLocation();
        return () => {
            cancelled = true;
        };
    }, [isAuthenticated, location.pathname, surveyNonMicReleased]);

    useEffect(() => {
        const hasStoredSession = isAuthenticated === true || Boolean(getAccessToken());
        const resumeRedirectPending = hasStoredSession && shouldResumeAuthenticatedRoute(location.pathname);
        if (!hasStoredSession || resumeRedirectPending || shouldDeferNonMicPermissions(location.pathname)) {
            return undefined;
        }

        const refreshLocation = () => {
            if (!readRuntimeLocationCurrent()) return;
            syncRuntimeLocationInBackground({
                pathname: location.pathname,
                captureReason: 'authenticated_session_refresh',
            });
        };
        const onVisibilityChange = () => {
            if (document.visibilityState === 'visible') refreshLocation();
        };
        const timer = window.setInterval(refreshLocation, RUNTIME_LOCATION_REFRESH_MS);
        window.addEventListener('focus', refreshLocation);
        document.addEventListener('visibilitychange', onVisibilityChange);
        return () => {
            window.clearInterval(timer);
            window.removeEventListener('focus', refreshLocation);
            document.removeEventListener('visibilitychange', onVisibilityChange);
        };
    }, [isAuthenticated, location.pathname, surveyNonMicReleased]);

    const dismissLocationGate = () => {
        try {
            window.sessionStorage.setItem(LOCATION_GATE_DISMISS_KEY, '1');
        } catch {
            // The in-memory flag still closes the gate for this view.
        }
        setLocationGateDismissed(true);
    };

    const requestLocationAccess = async () => {
        setLocationRequestBusy(true);
        setLocationRequestNote('');
        try {
            const result = await syncBrowserRuntimeLocation({
                pathname: location.pathname,
                captureReason: 'visible_user_location_consent',
                force: true,
            });
            if (readRuntimeLocationCurrent()) {
                setRuntimeLocationReady(true);
                setLocationPermissionState('ready');
            } else {
                setLocationPermissionState(result?.permission_state || result?.state || 'prompt');
                setLocationRequestNote(
                    result?.state === 'denied'
                        ? 'Location is blocked. Enable it in Chrome site settings, then try again.'
                        : result?.reason === 'location_timeout'
                        ? 'Chrome allowed access, but the location fix took too long. Make sure Windows Location Services are on, then try again.'
                        : 'Chrome allowed access, but no location was available. Make sure Windows Location Services are on, then try again.'
                );
            }
        } finally {
            setLocationRequestBusy(false);
        }
    };

    useEffect(() => {
        let timer = null;
        const onGlobalError = (event) => {
            const message = event?.detail?.message || '';
            if (!message) return;
            setGlobalError(message);
            if (timer) clearTimeout(timer);
            timer = setTimeout(() => setGlobalError(''), 5200);
        };
        window.addEventListener('dople:global-error', onGlobalError);
        return () => {
            if (timer) clearTimeout(timer);
            window.removeEventListener('dople:global-error', onGlobalError);
        };
    }, []);

    const authenticatedSession = isAuthenticated === true || Boolean(getAccessToken());
    const needsRuntimeLocation = authenticatedSession && !(runtimeLocationReady && readRuntimeLocationCurrent());
    const resumeRedirectPending = authenticatedSession && shouldResumeAuthenticatedRoute(location.pathname);
    const deferSurveyNonMicPermissions = shouldDeferNonMicPermissions(location.pathname);
    const locationGateActive = needsRuntimeLocation && !locationGateDismissed && !resumeRedirectPending && !deferSurveyNonMicPermissions && locationPromptAllowedOnPath(location.pathname) && !['checking', 'granted'].includes(locationPermissionState);
    const locationGatePassThrough = locationGateActive && isSurveyPath(location.pathname);

    return (
        <div>
            {locationGateActive ? (
                <div
                    className={`location-gate${locationGatePassThrough ? ' location-gate--pass-through' : ''}`}
                    role="dialog"
                    aria-modal={locationGatePassThrough ? 'false' : 'true'}
                    aria-label="Use your current location"
                >
                    <div className="location-gate__glow location-gate__glow--one" />
                    <div className="location-gate__glow location-gate__glow--two" />
                    <div className="location-gate__card">
                        <div className="location-gate__icon" aria-hidden="true">
                            <LocateFixed size={24} strokeWidth={1.8} />
                        </div>
                        <h1 className="location-gate__title">Use your location</h1>
                        <p className="location-gate__body">
                            Dople uses your location to show nearby events and recommendations.
                        </p>
                        {locationRequestNote ? (
                            <div className="location-gate__note" role="status">
                                {locationRequestNote}
                            </div>
                        ) : null}
                        <button
                            className="location-gate__button"
                            type="button"
                            onClick={requestLocationAccess}
                            disabled={locationRequestBusy}
                        >
                            <span>{locationRequestBusy ? 'Finding your location' : 'Use my current location'}</span>
                            {locationRequestBusy ? <span className="location-gate__spinner" /> : <ArrowRight size={18} />}
                        </button>
                        <button
                            type="button"
                            className="location-gate__skip"
                            onClick={dismissLocationGate}
                        >
                            Not now
                        </button>
                        <p className="location-gate__footnote">
                            Chrome may ask for permission.
                        </p>
                    </div>
                </div>
            ) : null}
            {globalError ? (
                <div
                    role="status"
                    aria-live="polite"
                    style={{
                        position: 'fixed',
                        top: '18px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        zIndex: 9999,
                        width: 'min(680px, calc(100% - 24px))',
                        padding: '0.72rem 0.88rem',
                        borderRadius: '12px',
                        border: '1px solid rgba(191, 219, 254, 0.95)',
                        background: 'rgba(239, 246, 255, 0.98)',
                        color: '#1e3a8a',
                        boxShadow: '0 14px 28px rgba(15, 23, 42, 0.12)',
                        fontSize: '0.92rem',
                        lineHeight: 1.45,
                        fontWeight: 600,
                    }}
                >
                    {globalError}
                </div>
            ) : null}
            {optionsReady?.stage === 'ready' || optionsReady?.stage === 'building' ? (
                <div
                    className={`global-trip-generation is-${optionsReady.stage === 'ready' ? 'complete' : 'building'}`}
                    aria-live="polite"
                >
                    <button
                        type="button"
                        className="global-trip-generation__open"
                        onClick={() => {
                            if (optionsReady.stage === 'ready') {
                                releaseOptionsReadyTitle();
                                navigate(`/trips/${optionsReady.tripId}`, { state: { optionsReady: true } });
                                setOptionsReady(null);
                                writeOptionsReady(null);
                                return;
                            }
                            navigate(`/trips/${optionsReady.tripId}`);
                        }}
                        aria-label={optionsReady.stage === 'ready' ? 'Your options are ready' : 'Creating your trip options'}
                    >
                        <span className="global-trip-generation__icon">
                            {optionsReady.stage === 'ready' ? <CheckCircle2 size={18} /> : <Loader2 className="global-trip-generation__spinner" size={18} />}
                        </span>
                        <span>
                            <strong>{optionsReady.stage === 'ready' ? 'Your options are ready' : 'Creating your trip options'}</strong>
                            <small>
                                {optionsReady.stage === 'ready'
                                    ? 'Open them to compare the day-by-day preview.'
                                    : 'This usually takes about two minutes. You can leave this page.'}
                            </small>
                        </span>
                        <ArrowRight size={17} />
                    </button>
                    <button
                        type="button"
                        className="global-trip-generation__dismiss"
                        onClick={dismissOptionsReady}
                        aria-label="Dismiss options notification"
                        title="Dismiss"
                    >
                        <X size={15} />
                    </button>
                </div>
            ) : null}
            {activeTripGeneration ? (
                <div
                    className={`global-trip-generation is-${activeTripGeneration.stage || 'starting'}`}
                    aria-live="polite"
                >
                    <button
                        type="button"
                        className="global-trip-generation__open"
                        onClick={() => navigate(`/trips/${activeTripGeneration.tripId}`)}
                        aria-label={`Open ${activeTripGeneration.destination || 'trip'} generation status`}
                    >
                    <span className="global-trip-generation__icon">
                        {activeTripGeneration.stage === 'complete' ? (
                            <CheckCircle2 size={18} />
                        ) : activeTripGeneration.stage === 'failed' ? (
                            <AlertCircle size={18} />
                        ) : (
                            <Loader2 className="global-trip-generation__spinner" size={18} />
                        )}
                    </span>
                    <span>
                        <strong>
                            {activeTripGeneration.stage === 'complete'
                                ? `${activeTripGeneration.destination || 'Your itinerary'} is ready`
                                : activeTripGeneration.stage === 'failed'
                                ? `${activeTripGeneration.destination || 'Your itinerary'} needs another pass`
                                : `Building ${activeTripGeneration.destination || 'your itinerary'}`}
                        </strong>
                        <small>
                            {tripGenerationStageCopy(activeTripGeneration.stage)}
                            {activeTripGeneration.stage !== 'complete' &&
                            activeTripGeneration.stage !== 'failed' &&
                            activeTripGeneration.estimate?.label
                                ? ` · Estimated ${activeTripGeneration.estimate.label}`
                                : ''}
                        </small>
                    </span>
                        <ArrowRight size={17} />
                    </button>
                    <button
                        type="button"
                        className="global-trip-generation__dismiss"
                        onClick={clearActiveTripGeneration}
                        aria-label="Dismiss trip generation notification"
                        title="Dismiss"
                    >
                        <X size={15} />
                    </button>
                </div>
            ) : null}
            {props.children}
        </div>
    );
};

const mapStateToProps = (state) => ({
    isAuthenticated: state.auth.isAuthenticated,
});

export default connect(mapStateToProps, { checkAuthenticated, load_user })(Layout);
